<template>
  <div id="app">
    <top-bar v-if="isDisabledHeader()"/>
    <main className="main">
      <router-view/>
    </main>
    <status-bar ref="statusBar"></status-bar>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {debounce} from 'lodash';
import axios from 'axios';
import config from '@/config';

import TopBar from "@/components/top-bar";

export default {
  name: 'app',
  components: {TopBar},
  data() {
    return {
      isPositionFixed: false,
      isModalOpened: false,
      isDarkMode: null,
      modalProps: {
        type: Object,
        default: () => ({})
      },
      statusCheckInterval: null,
    }
  },

  watch: {
    isPositionFixed: function () {
      this.isPositionFixed ? this.$bus.emit('set-position-fixed') : this.$bus.emit('remove-position-fixed')
    },
    '$route'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$bus.emit('close-mobile-menu')
      }
    },
    // Следим за изменением токена в cookies
    '$cookies.get("token")': {
      handler(newToken) {
        if (newToken) {
          // Если токен появился (пользователь залогинился), запускаем проверку
          console.log('Token detected, starting user status check');
          this.startUserStatusCheck();
        } else {
          // Если токен удален, останавливаем проверку
          this.stopUserStatusCheck();
        }
      },
      immediate: false
    }
  },

  created() {
    window.addEventListener('resize', this.onResize, {passive: true})
  },

  mounted() {
    this.$bus.on('set-position-fixed', () => {
      this.isPositionFixed = true
      document.querySelector('body').classList.add('overflow--hidden')
    })

    this.$bus.on('remove-position-fixed', () => {
      this.isPositionFixed = false
      document.querySelector('body').classList.remove('overflow--hidden')
    })

    this.$bus.on('open-mobile-menu', () => {
      this.openMenu()
      this.$bus.emit('set-position-fixed')
    })

    this.$bus.on('close-mobile-menu', () => {
      this.closeMenu()
      this.$bus.emit('remove-position-fixed')
    })

    this.startHistoryLength = 0

    // Запускаем проверку статуса пользователя только если есть токен
    const token = this.$cookies.get('token');
    if (token) {
      this.startUserStatusCheck();
    }
  },

  beforeDestroy() {
    this.stopUserStatusCheck();
  },

  computed: {
    ...mapGetters(['isDesktop']),
    ...mapGetters(['isMenuOpened']),
    ...mapGetters(['getOpenedModals']),
  },

  methods: {
    ...mapActions(['openMenu', 'closeMenu']),

    // Запуск периодической проверки
    startUserStatusCheck() {
      // Останавливаем предыдущий интервал, если он был
      this.stopUserStatusCheck();

      console.log('Starting user status check');
      // Проверяем сразу
      this.checkUserStatus();

      // Затем каждую минуту
      this.statusCheckInterval = setInterval(() => {
        this.checkUserStatus();
      }, 60000);
    },

    // Остановка периодической проверки
    stopUserStatusCheck() {
      if (this.statusCheckInterval) {
        console.log('Stopping user status check');
        clearInterval(this.statusCheckInterval);
        this.statusCheckInterval = null;
      }
    },

    // Метод для перезапуска проверки (вызывается извне)
    restartUserStatusCheck() {
      console.log('Restarting user status check');
      const token = this.$cookies.get('token');
      if (token) {
        this.startUserStatusCheck();
      } else {
        this.stopUserStatusCheck();
      }
    },

    async checkUserStatus() {
      try {
        const token = this.$cookies.get('token');
        if (!token) {
          console.log('No token found, stopping check');
          this.stopUserStatusCheck();
          return;
        }

        // Получаем userId из localStorage
        let userId = null;
        const userConfig = localStorage.getItem('userconfig');

        if (userConfig) {
          try {
            const userData = JSON.parse(userConfig);
            userId = userData.id;
          } catch (e) {
            console.error('Error parsing userconfig:', e);
          }
        }

        if (!userId) {
          userId = this.$store?.state?.user?.id || this.$cookies.get('userId');
        }

        if (!userId) {
          console.warn('User ID not found');
          return;
        }

        console.log('Checking user status for ID:', userId);

        const response = await axios.get(`${config.api}users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const userData = response.data.data;
        if (userData.is_active === 0) {
          console.log('User is inactive, starting logout');
          this.removeUserID(userData);
        }

      } catch (error) {
        console.error('Error checking user status:', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.forceLogout();
        }
      }
    },

    removeUserID(data) {
      console.log('data.is_active', data.is_active);

      // Сразу проверяем, если is_active === 0 - разлогиниваем
      if (data.is_active === 0) {
        console.log('User is inactive based on data, force logout');
        this.forceLogout(data);
        return;
      }

      // Дополнительная проверка через GET
      axios.get(`${config.api}users/${data.id}`)
          .then((response) => {
            const userData = response.data.data;
            if (userData.is_active === 0) {
              console.log('start forceLogout');
              this.forceLogout(data);
            }
          })
          .catch(error => {
            console.error('Error in removeUserID:', error);
            if (error.response?.status === 404 || error.response?.status === 401 || error.response?.status === 403) {
              this.forceLogout(data);
            }
          });
    },

    forceLogout(userData = null) {
      console.log('Force logout');

      // Удаляем куки
      this.$cookies.remove('token');
      this.$cookies.remove('userId');

      // Останавливаем проверку статуса
      this.stopUserStatusCheck();

      // Обновляем состояние в store если есть данные
      if (userData && this.$store) {
        this.$store.commit('updateClient', userData);
      }

      // Закрываем мобильное меню
      this.$bus.emit('close-mobile-menu');

      // Убираем фиксированную позицию
      this.$bus.emit('remove-position-fixed');

      // Перенаправляем на главную
      this.$router.push('/');

      // Показываем уведомление
      this.$bus.emit('show-notification', {
        message: 'Ваш аккаунт был деактивирован',
        type: 'warning'
      });
    },

    closeMobileMenu() {
      this.$bus.emit('close-mobile-menu')
    },

    watch: {
      '$route'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$bus.emit('close-mobile-menu');
        }

        // Проверяем, нужно ли запустить проверку статуса
        const token = this.$cookies.get('token');
        if (token && !this.statusCheckInterval) {
          console.log('Token detected on route change, starting status check');
          this.startUserStatusCheck();
        }
      },
    },

    onResize: debounce(function () {
      if (this.windowWidth !== window.innerWidth) {
        this.windowWidth = window.innerWidth
      }
    }, 500),

    isDisabledHeader() {
      let pages = [
        'home',
        'print',
      ];
      return pages.includes(this.$router.currentRoute.name) ? false : true
    }
  }
}
</script>

<style lang="scss">
@import "assets/scss/main.scss";
</style>